import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  signupConfirmationCodeAPI,
  resendOTPAPI,
} from "../../redux/features/authSlice";
import toast from "react-hot-toast";
import CustomButton from "../CustomButton/CustomButton";
import { useSearchParams } from "react-router-dom";

export default function ConfirmationCodeForm() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useSelector((state) => state.auth);

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  const validationSchema = Yup.object({
    otp: Yup.array()
      .of(
        Yup.string()
          .matches(/^[0-9]$/, "Must be a single digit")
          .required("Digit required")
      )
      .length(6, "OTP must be exactly 6 digits"),
  });

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleChange = (index, e, setFieldValue) => {
    const value = e.target.value;
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setFieldValue("otp", newOtp);
      if (value && index < 5) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e, setFieldValue) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^[0-9]{6}$/.test(pastedData)) {
      const newOtp = pastedData.split("").slice(0, 6);
      setOtp(newOtp);
      setFieldValue("otp", newOtp);
      inputRefs.current[5].focus();
    } else {
      toast.error("Please paste a valid 6-digit OTP", {
        position: "top-center",
      });
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const obj = {
      email: auth.email,
      otp: values.otp.join(""),
    };

    try {
      const res = await dispatch(signupConfirmationCodeAPI(obj)).unwrap();
      if (res.success) {
        toast.success(res?.message, {
          position: "top-center",
        });

        if(searchParams.get("reset")){
            if (res?.data?.user?.role?.name === "Instructor") {
              navigate("/instructor-reset-password");
            } else if (res?.data?.user?.role?.name === "Student") {
              navigate("/student-reset-password");
            } else if (res?.data?.user?.role?.name === "Admin") {
              navigate("/admin-reset-password");
            }
        } else{
          if (
            res?.data?.user?.status === "pending" &&
            res?.data?.user?.role?.name === "Student"
          ) {
            // navigate("/add-education");
            navigate("/choose-plan");
          } else if (
            res?.data?.user?.status === "pending" &&
            res?.data?.user?.role?.name === "Instructor"
          ) {
            navigate("/instructor-add-education");
          } else {
            if (res?.data?.user?.role?.name === "Instructor") {
              navigate("/instructor-reset-password");
            } else if (res?.data?.user?.role?.name === "Student") {
              navigate("/student-reset-password");
            } else if (res?.data?.user?.role?.name === "Admin") {
              navigate("/admin-reset-password");
            }
          }
        }
      }
    } catch (error) {
      toast.error(error?.message || "Invalid OTP", {
        position: "top-center",
      });
      // setOtp(["", "", "", "", "", ""]);
      // values.otp = ["", "", "", "", "", ""];
    } finally {
      setSubmitting(false);
    }
  };

  const resendHandler = async () => {
    try {
      const res = await dispatch(resendOTPAPI({ email: auth.email })).unwrap();
      toast.success(res.message, {
        position: "top-center",
      });
      setCanResend(false);
      setResendTimer(30);
      setOtp(["", "", "", "", "", ""]);
    } catch (error) {
      toast.error(error?.message || "Failed to resend OTP", {
        position: "top-center",
      });
    }
  };

  return (
    <Formik
      initialValues={{ otp }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({ isSubmitting, values, setFieldValue, dirty, isValid }) => (
        <Form className="bg-white auth-form-holder mx-auto px-20 px-sm-30 px-md-40 pt-40 pt-md-50 pb-30">
          <div className="mb-12 mb-md-16">
            <label
              htmlFor="otp[0]"
              className="form-label black-100 fw-semibold mb-8 mb-lg-12"
            >
              Enter OTP
            </label>
            <div className="d-flex justify-content-between gap-3">
              {values.otp.map((digit, index) => (
                <div key={index} className="d-flex flex-column align-items-center">
                  <Field
                    name={`otp[${index}]`}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleChange(index, e, setFieldValue)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={(e) => handlePaste(e, setFieldValue)}
                    innerRef={(el) => (inputRefs.current[index] = el)}
                    className="text-center form-control-otp"
                  />
                  <ErrorMessage
                    name={`otp[${index}]`}
                    component="div"
                    className="text-danger mt-1"
                    style={{ fontSize: "0.75rem" }}
                  />
                </div>
              ))}
            </div>
          </div>
          <CustomButton
            type="submit"
            label={isSubmitting ? "Verifying..." : "Verify"}
            classes="btn btn-primary fw-bold w-100 mb-10"
            isSubmitting={isSubmitting}
            disabled={isSubmitting || !isValid || !dirty}
          />
          <div className="text-center text-md pt-20">
            {canResend ? (
              <span
                className="cursor-pointer fw-semibold theme-color"
                onClick={resendHandler}
              >
                Resend OTP
              </span>
            ) : (
              <>
                Resend OTP in{" "}
                <span className="fw-semibold">{resendTimer}s</span>
              </>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
}